import * as Api from '@ViewModels';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useUserSession } from '../../models/hooks/appStateHooks';

interface IParams {
	actionItemId: string;
	isCompleted: boolean;
	impersonationContext?: Api.IImpersonationContext;
}

export function useActionItemToggleCompleteMutation({
	onError,
	onSuccess,
}: {
	onError?: (error: Api.IOperationResultNoValue) => void;
	onSuccess?: (data: Api.IActionItem, variables: IParams) => void;
} = {}) {
	const userSession = useUserSession();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async ({ actionItemId, isCompleted, impersonationContext }: IParams) => {
			const result = await userSession.webServiceHelper.callAsync<Api.IActionItem>(
				Api.ImpersonationBroker.composeApiUrl({
					urlPath: `actionitem/${encodeURIComponent(actionItemId)}/${isCompleted ? 'complete' : 'incomplete'}`,
					impersonationContext,
				}),
				'POST',
				null
			);
			return result as Api.IActionItem;
		},
		onSuccess: (data, variables) => {
			// Target and invalidate timeline queries specifically
			queryClient.invalidateQueries({
				predicate: query => {
					const queryKey = query.queryKey;
					// Specifically target infinite timeline queries
					return (
						queryKey.length > 0 && typeof queryKey[0] === 'string' && queryKey[0].includes('infiniteContactTimeline')
					);
				},
			});

			// Also invalidate contact queries that might include action item data
			queryClient.invalidateQueries({
				predicate: query => {
					const queryKey = query.queryKey;
					return (
						queryKey.length > 0 &&
						typeof queryKey[0] === 'string' &&
						queryKey[0].includes('contact') &&
						!queryKey[0].includes('Timeline')
					);
				},
			});

			// Force refetch of timeline queries to ensure immediate update
			queryClient.refetchQueries({
				predicate: query => {
					const queryKey = query.queryKey;
					return (
						queryKey.length > 0 && typeof queryKey[0] === 'string' && queryKey[0].includes('infiniteContactTimeline')
					);
				},
			});

			onSuccess?.(data, variables);
		},
		onError,
	});
}
